import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ConfigurationOption {
  name: string;
  value: string;
}

interface Configuration {
  id: string;
  name: string;
  parameters: {
    projection?: string[];
    bitdepth?: string[];
    product_type?: string[];
    radiometric_processing?: string[];
  };
}

interface ConfigurationDataOptions {
  projection: string[];
  bitdepth: string[];
  radiometric_processing: string[];
  product_type: string[];
}

interface ConfigurationSettingsData {
  id: string;
  name: string;
  bitDepth?: string;
  projections?: string;
  radioMetricProcessing?: string;
  grazingAngle?: {
    min: number;
    max: number;
  };
  polarization?: string;
}

interface ConfigSettingsProps {
  configurationDataOptions: ConfigurationDataOptions;
  configurationOptions: Array<{ name: string; value: string }>;
  configurationSettingsData: ConfigurationSettingsData;
  configurations: Array<{
    id: string;
    name: string;
    parameters?: {
      projection?: string[];
      bitdepth?: string[];
      radiometric_processing?: string[];
      product_type?: string[];
    };
  }>;
  setConfigurationDataOptions: React.Dispatch<React.SetStateAction<ConfigurationDataOptions>>;
  setConfigurationSettingsData: React.Dispatch<React.SetStateAction<ConfigurationSettingsData>>;
}

function ConfigSettings({
  configurationSettingsData,
  setConfigurationSettingsData,
  configurations,
  configurationOptions,
  setConfigurationDataOptions,
  configurationDataOptions,
}: ConfigSettingsProps): React.ReactElement {
  const { t } = useTranslation('provider');
  const handleConfigurationChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    const filteredConfiguration = configurations?.find(item => item?.id === id);

    if (filteredConfiguration) {
      setConfigurationSettingsData(prev => ({
        ...prev,
        name: filteredConfiguration.name,
        id: filteredConfiguration.id,
      }));

      const parameters = filteredConfiguration.parameters;
      setConfigurationDataOptions({
        projection: parameters?.projection ?? [],
        bitdepth: parameters?.bitdepth ?? [],
        product_type: parameters?.product_type ?? [],
        radiometric_processing: parameters?.radiometric_processing ?? [],
      });
    }
  };

  const handleSettingChange = (
    field: keyof Omit<ConfigurationSettingsData, 'id' | 'name'>,
    event: SelectChangeEvent<string>
  ) => {
    const data = { ...configurationSettingsData, [field]: event.target.value };
    setConfigurationSettingsData(data);
  };

  return (
    <Box display="flex" gap={3} flexDirection="column">
      <FormControl variant="filled" fullWidth>
        <InputLabel id="configuration-name-label">{t('configurationName')}</InputLabel>
        <Select
          labelId="configuration-name-label"
          id="configuration-name-select"
          label={t('configurationName')}
          value={configurationSettingsData.id}
          MenuProps={{
            className: 'select-menu',
          }}
          onChange={handleConfigurationChange}
        >
          {configurationOptions?.map((item, index) => (
            <MenuItem key={`${index}`} value={item?.value}>
              {item?.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {configurationDataOptions?.bitdepth && configurationDataOptions.bitdepth.length > 0 && (
        <FormControl variant="filled" fullWidth>
          <InputLabel id="bit-depth-label">{t('bitDepth')}</InputLabel>
          <Select
            labelId="bit-depth-label"
            id="bit-depth-select"
            label={t('bitDepth')}
            value={configurationSettingsData.bitDepth ?? ''}
            MenuProps={{
              className: 'select-menu',
            }}
            onChange={e => handleSettingChange('bitDepth', e)}
          >
            {configurationDataOptions?.bitdepth?.map((item, index) => (
              <MenuItem key={`${index}`} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {configurationDataOptions?.projection && configurationDataOptions.projection.length > 0 && (
        <FormControl variant="filled" fullWidth>
          <InputLabel id="projections-label">{t('projections')}</InputLabel>
          <Select
            labelId="projections-label"
            id="projections-select"
            label={t('projections')}
            value={configurationSettingsData.projections ?? ''}
            MenuProps={{
              className: 'select-menu',
            }}
            onChange={e => handleSettingChange('projections', e)}
          >
            {configurationDataOptions?.projection?.map((item, index) => (
              <MenuItem key={`${index}`} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {configurationDataOptions?.radiometric_processing &&
        configurationDataOptions.radiometric_processing.length > 0 && (
          <FormControl variant="filled" fullWidth>
            <InputLabel id="radio-metric-label">{t('radioMetric')}</InputLabel>
            <Select
              labelId="radio-metric-label"
              id="radio-metric-select"
              label={t('radioMetric')}
              value={configurationSettingsData.radioMetricProcessing ?? ''}
              MenuProps={{
                className: 'select-menu',
              }}
              onChange={e => handleSettingChange('radioMetricProcessing', e)}
            >
              {configurationDataOptions?.radiometric_processing?.map((item, index) => (
                <MenuItem key={`${index}`} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
    </Box>
  );
}

export default ConfigSettings;
